/**
 * Linking Content Library (v2) content into a course.
 *
 * The important idea, and the reason this is not just "copy a block": a linked component keeps
 * a pointer to the library block it came from — its UPSTREAM. The course holds a real block an
 * author can adjust locally, and the platform tracks whether the library has moved on since.
 * That is what makes a library worth having: fix a typo once, and every course carrying that
 * block can take the update.
 *
 * The link is established at creation time. `POST /xblock` with `library_content_key` sets the
 * new block's `upstream` and immediately syncs it from the library; if the reference is bad the
 * platform deletes the half-made block and returns 400, so there is no partial state to clean
 * up. Verified in `cms/.../view_handlers.py` `_create_block`.
 */

import { openedx } from './openedx';
import { listLibraries, type StudioLibrary } from './studio';

/* ------------------------------------------------------------------- browsing ---- */

/**
 * Libraries this user can see, inside this academy.
 *
 * Delegates to the tenant listing rather than repeating it: the picker and the Content Library
 * page must never disagree about which libraries exist.
 */
export const listLibrariesForPicker = (tenantSlug: string, search?: string) =>
  listLibraries(tenantSlug, search ? { search } : {});

/** One block inside a library, as `LibraryXBlockMetadataSerializer` returns it. */
export interface LibraryBlockSummary {
  /** The usage key — `lb:ORG:slug:type:id`. This is what links into a course. */
  id: string;
  block_type: string;
  display_name: string;
  published_display_name: string | null;
  has_unpublished_changes: boolean;
  last_published: string | null;
  /** Draft-modified time. Named `modified`, not `updated`. */
  modified: string | null;
  tags_count: number;
  can_stand_alone: boolean;
}

export interface BlockPage {
  results: LibraryBlockSummary[];
  count: number;
  num_pages: number;
  current_page: number;
}

/**
 * Blocks in a library.
 *
 * Always paginated here, unlike the library list — this endpoint calls
 * `get_paginated_response` unconditionally, with a default page size of 10 and a server
 * maximum of 100.
 */
export function listLibraryBlocks(
  libKey: string,
  options: { page?: number; pageSize?: number; search?: string } = {},
) {
  const query = new URLSearchParams();
  query.set('page', String(options.page ?? 1));
  query.set('page_size', String(Math.min(options.pageSize ?? 24, 100)));
  if (options.search) query.set('text_search', options.search);
  return openedx.get<BlockPage>(
    `/api/libraries/v2/${encodeURIComponent(libKey)}/blocks/?${query}`,
  );
}

/**
 * A library's own detail record.
 *
 * Needed because the LIST endpoint lies about several fields: it returns `num_blocks: 0`,
 * `description: ""`, `can_edit_library: false` and null timestamps regardless of the real
 * values. Anything shown beyond title/org/slug has to come from the detail view.
 */
export const getLibraryDetail = (libKey: string) =>
  openedx.get<StudioLibrary>(`/api/libraries/v2/${encodeURIComponent(libKey)}/`);

/**
 * Rendered preview of a library block, for an iframe.
 *
 * `@published` on purpose. Linking pulls the PUBLISHED version, so previewing the draft would
 * show an author content they are not about to get — the same reason Studio's own picker uses
 * `variant=published`.
 *
 * The iframe form rather than the JSON `view/` endpoint: the JSON returns a fragment plus a list
 * of `resources` that the caller must inject in the right order, and a problem or a
 * drag-and-drop is unusable without its own JS. The embed view is a self-contained document that
 * loads its own resources, and it is `@xframe_options_exempt`.
 *
 * Upstream marks this endpoint unstable ("subject to changes after Sumac").
 */
export const libraryBlockPreviewUrl = (blockKey: string) =>
  `/xblocks/v2/${encodeURIComponent(blockKey)}/embed/student_view/?version=published`;

/* --------------------------------------------------------------------- linking ---- */

/** What the platform reports back about assets that came along with a linked block. */
export interface StaticFileNotices {
  new_files?: string[];
  conflicting_files?: string[];
  error_files?: string[];
}

export interface LinkResult {
  locator: string;
  upstreamRef?: string;
  static_file_notices?: StaticFileNotices;
}

/**
 * Insert a library block under a parent, linked to its library original.
 *
 * `category` is the library block's own type: the course-side block is a real block of that
 * type whose content is synced from the library, not a wrapper around it. Sending a different
 * category would create the wrong kind of block and then try to sync incompatible content
 * into it.
 *
 * `parentKey` IS NOT ALWAYS A UNIT, and nothing here has to change for that to be true. The
 * platform's `_create_block` reads `parent_locator` and creates under whatever it names;
 * Studio's own Problem Bank picker (`cms/static/js/views/pages/container.js`,
 * `showSelectV2LibraryContent`) posts this identical body with the ITEM BANK's key as the
 * parent. A bank with no children renders zero characters to a learner, so this is the call
 * that makes the type usable at all.
 */
export const linkLibraryBlock = (
  parentKey: string,
  block: Pick<LibraryBlockSummary, 'id' | 'block_type'>,
) =>
  openedx.post<LinkResult>('/api/authoring/v1/xblock', {
    parent_locator: parentKey,
    category: block.block_type,
    library_content_key: block.id,
  });

/** Did linking bring assets that clash with something already in the course? */
export function assetWarnings(notices: StaticFileNotices | undefined): string[] {
  if (!notices) return [];
  const out: string[] = [];
  if (notices.conflicting_files?.length) {
    out.push(
      `${notices.conflicting_files.length} file(s) already existed in this course with the same name and were left as they were: ${notices.conflicting_files.join(', ')}.`,
    );
  }
  if (notices.error_files?.length) {
    out.push(
      `${notices.error_files.length} file(s) could not be copied: ${notices.error_files.join(', ')}.`,
    );
  }
  return out;
}

/* ------------------------------------------------------------- staying in sync ---- */

/**
 * The "downstreams" API — everything about a course block's link to its library original.
 *
 * Contract read from the module docstring of
 * `cms/djangoapps/contentstore/rest_api/v2/views/downstreams.py`, which documents it in full.
 * These are DRF v2 views, so the JWT authenticates them and no wrapper of ours is involved.
 *
 * Upstream marks this API `[🛑 UNSTABLE]`. It is still the only way to do any of this, and the
 * alternative is not offering library updates at all — but it is a reason to keep every call to
 * it inside this file, so a rename lands in one place.
 */
const DOWNSTREAM = '/api/contentstore/v2/downstreams';

/** The link's state, as `UpstreamLink.to_json()` returns it. */
export interface UpstreamLink {
  upstream_ref: string | null;
  /** Version of the library block this course copy was last synced from. */
  version_synced: number | null;
  /** Latest published version in the library. Higher than `version_synced` means an update. */
  version_available: number | null;
  /** A version the author explicitly skipped; the prompt stays hidden until a newer one. */
  version_declined: number | null;
  ready_to_sync: boolean;
  error_message: string | null;
  /**
   * Fields the author has changed locally.
   *
   * This is what makes syncing a decision rather than a button: taking the update can either
   * keep these or overwrite them, and only the author knows which they meant.
   */
  downstream_customized: string[];
}

export const getUpstreamLink = (blockKey: string) =>
  openedx.get<UpstreamLink>(`${DOWNSTREAM}/${blockKey}`);

/**
 * Take the library's latest version.
 *
 * `override_customizations` decides what happens to local edits. Defaulting it to false is the
 * safe direction — an author's own wording survives — and the caller offers the other option
 * explicitly rather than silently discarding work.
 */
export const syncFromUpstream = (
  blockKey: string,
  options: { overrideCustomizations?: boolean; keepCustomFields?: string[] } = {},
) =>
  openedx.post<UpstreamLink & { static_file_notices?: StaticFileNotices }>(
    `${DOWNSTREAM}/${blockKey}/sync`,
    {
      override_customizations: options.overrideCustomizations ?? false,
      ...(options.keepCustomFields ? { keep_custom_fields: options.keepCustomFields } : {}),
    },
  );

/** Skip this version. The prompt returns when the library publishes a newer one. */
export const declineSync = (blockKey: string) =>
  openedx.delete<void>(`${DOWNSTREAM}/${blockKey}/sync`);

/**
 * Cut the link, keeping the content.
 *
 * The block stays in the course exactly as it is; it simply stops tracking the library. Not
 * reversible from here, which the confirmation says.
 */
export const unlinkFromUpstream = (blockKey: string) =>
  openedx.delete<void>(`${DOWNSTREAM}/${blockKey}`);

/** Per-library rollup for one course: how much of it is behind, and by when. */
export interface CourseLinkSummary {
  upstream_context_title: string;
  upstream_context_key: string;
  ready_to_sync_count: number;
  total_count: number;
  last_published_at: string | null;
}

export const courseLinkSummary = (courseKey: string) =>
  openedx.get<CourseLinkSummary[]>(`${DOWNSTREAM}/${encodeURIComponent(courseKey)}/summary`);
